import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { queryKeys } from '@/hooks/query-keys'
import { hashKey } from '@tanstack/react-query'

import { isValidUUID } from '@scrambl/shared'

import type { RecordQueryScope } from './record-query-scope'
import { isRecordQueryInScope, scopedRecordKey } from './record-query-scope'

type Target = { key: QueryKey; exact?: boolean }
type Change = 'definition' | 'run' | 'delete'
const clients = new WeakMap<
  QueryClient,
  Map<string, ReturnType<typeof createEntry>>
>()
const WINDOW_MS = 120
const keys = queryKeys.workflows

function createEntry(client: QueryClient, scope: RecordQueryScope) {
  let subscribers = 0
  let disposed = false
  let running = false
  let timer: ReturnType<typeof setTimeout> | undefined
  const pending = new Map<string, Target>()
  const seen = new Map<string, number>()
  const suspended = new Map<string, number>()
  const current = () => !disposed && !scope.signal.aborted
  const keyFor = (key: QueryKey) => scopedRecordKey(key, scope)
  const allTarget = hashKey([keys.all, false])
  const enqueue = (key: QueryKey, exact = false) => {
    if (pending.has(allTarget)) return
    // A long request plus a large import must not retain an unbounded ID set.
    // Collapse to one current-session refresh, retaining draft protection.
    if (pending.size >= 512) {
      pending.clear()
      pending.set(allTarget, { key: keys.all })
    } else pending.set(hashKey([key, exact]), { key, exact })
  }
  const inScope = (query: { queryKey: QueryKey }) =>
    isRecordQueryInScope(query.queryKey, scope)
  const definitionSuspended = (key: QueryKey) =>
    key[1] === 'detail' && key.length === 4 && suspended.has(key[2] as string)
  function schedule() {
    if (current() && !running && timer === undefined && pending.size)
      timer = setTimeout(flush, WINDOW_MS)
  }
  function flush() {
    timer = undefined
    if (!current() || running || !pending.size) return
    const targets = [...pending.values()]
    pending.clear()
    const matches = (query: { queryKey: QueryKey }) =>
      inScope(query) &&
      targets.some(
        ({ key, exact }) =>
          (!exact || key.length === query.queryKey.length) &&
          hashKey(query.queryKey.slice(0, key.length)) === hashKey(key),
      )
    void client.invalidateQueries({
      queryKey: keys.all,
      predicate: matches,
      refetchType: 'none',
    })
    const canRefresh = (query: { queryKey: QueryKey }) =>
      matches(query) && !definitionSuspended(query.queryKey)
    const earlierFetches = client
      .getQueryCache()
      .findAll({ queryKey: keys.all, predicate: canRefresh })
      .filter(
        query => query.isActive() && query.state.fetchStatus === 'fetching',
      )
      .map(query => query.queryKey)
    running = true
    void client
      .refetchQueries(
        { queryKey: keys.all, predicate: canRefresh, type: 'active' },
        { cancelRefetch: false },
      )
      .finally(() => {
        running = false
        if (!current()) return
        // A pre-existing request may have snapshotted before this notice.
        earlierFetches.forEach(key => enqueue(key, true))
        schedule()
      })
  }
  function cancel() {
    clearTimeout(timer)
    timer = undefined
    pending.clear()
    seen.clear()
  }
  scope.signal.addEventListener('abort', cancel, { once: true })
  return {
    retain() {
      subscribers++
    },
    release() {
      if (--subscribers > 0) return false
      disposed = true
      cancel()
      scope.signal.removeEventListener('abort', cancel)
      return true
    },
    suspend(id: string) {
      suspended.set(id, (suspended.get(id) ?? 0) + 1)
    },
    resume(id: string) {
      const remaining = (suspended.get(id) ?? 1) - 1
      if (remaining > 0) {
        suspended.set(id, remaining)
        return
      }
      suspended.delete(id)
      const key = keyFor(keys.detail(id))
      if (client.getQueryState(key)?.isInvalidated) {
        enqueue(key, true)
        schedule()
      }
    },
    refreshAll() {
      if (!current()) return
      enqueue(keys.lists())
      enqueue(keys.details())
      schedule()
    },
    notify(id: string, change: Change, eventId?: string) {
      if (!current()) return
      const now = Date.now()
      for (const [key, at] of seen) if (now - at > 30_000) seen.delete(key)
      if (eventId && isValidUUID(eventId)) {
        const eventKey = hashKey([id, change, eventId])
        if (seen.has(eventKey)) return
        seen.set(eventKey, now)
        if (seen.size > 512) seen.delete(seen.keys().next().value!)
      }
      const detail = keyFor(keys.detail(id))
      if (change === 'delete') {
        const filter = { queryKey: detail, predicate: inScope }
        void client.cancelQueries(filter)
        client.removeQueries(filter)
        client.setQueriesData(
          { queryKey: keys.lists(), predicate: inScope },
          (old: unknown) => {
            if (Array.isArray(old)) return old.filter(row => row?.id !== id)
            if (
              old &&
              typeof old === 'object' &&
              'data' in old &&
              Array.isArray(old.data)
            )
              return { ...old, data: old.data.filter(row => row?.id !== id) }
            return old
          },
        )
      }
      if (change === 'run') enqueue([...detail, 'runs'])
      else {
        enqueue(keyFor(keys.lists()))
        if (change !== 'delete') enqueue(detail, true)
      }
      schedule()
    },
  }
}

/** Shared by app and page listeners: one bounded refresh, then one trailing read. */
export function subscribeWorkflowInvalidation(
  client: QueryClient,
  scope: RecordQueryScope,
) {
  let entries = clients.get(client)
  if (!entries) {
    entries = new Map()
    clients.set(client, entries)
  }
  const key = hashKey(scope.key)
  let entry = entries.get(key)
  if (!entry) {
    entry = createEntry(client, scope)
    entries.set(key, entry)
  }
  const selected = entry
  selected.retain()
  let disposed = false
  let suspendedId: string | null = null
  return {
    refreshAll() {
      if (!disposed) selected.refreshAll()
    },
    notify(id: string, change: Change, eventId?: string) {
      if (!disposed) selected.notify(id, change, eventId)
    },
    suspend(id: string | null) {
      if (disposed || suspendedId === id) return
      if (suspendedId) selected.resume(suspendedId)
      suspendedId = id
      if (id) selected.suspend(id)
    },
    dispose() {
      if (disposed) return
      disposed = true
      if (suspendedId) selected.resume(suspendedId)
      if (selected.release()) entries.delete(key)
    },
  }
}
